import { getEventDate } from './get-event-date';
import type { EventData } from './get-event-date';
import type { ListEventsResponseEntry } from './list-events-from-google';

export type AtRiskAppointmentCandidate = EventData &
  Pick<ListEventsResponseEntry, 'id' | 'reminders_paused_by_quota'>;

export interface AtRiskAppointmentsOptions<
  T extends AtRiskAppointmentCandidate,
> {
  events: readonly T[];
  now: Date;
  visibleRange: { start: Date; end: Date };
  rowLimit: number;
}

export interface AtRiskAppointments<T extends AtRiskAppointmentCandidate> {
  count: number;
  next: T | null;
  rows: T[];
}

/**
 * Upcoming appointments in the visible range whose reminders the quota pause
 * blocks, soonest first. Reads only the server's per-occurrence flag.
 */
export function getAtRiskAppointments<T extends AtRiskAppointmentCandidate>(
  options: AtRiskAppointmentsOptions<T>,
): AtRiskAppointments<T> {
  const { events, now, visibleRange, rowLimit } = options;
  const atRisk = dedupeOccurrences(events)
    .filter((event) => event.reminders_paused_by_quota === true)
    .map((event) => ({ event, startMs: getEventDate(event).getTime() }))
    .filter(
      ({ startMs }) =>
        startMs > now.getTime() &&
        startMs >= visibleRange.start.getTime() &&
        startMs < visibleRange.end.getTime(),
    )
    .sort((left, right) => left.startMs - right.startMs)
    .map(({ event }) => event);

  return {
    count: atRisk.length,
    next: atRisk[0] ?? null,
    rows: atRisk.slice(0, Math.max(rowLimit, 0)),
  };
}

function dedupeOccurrences<T extends AtRiskAppointmentCandidate>(
  events: readonly T[],
): T[] {
  const byOccurrence = new Map<string, T>();
  for (const event of events) {
    const key = `${event.id}-${event.event_date}`;
    if (!byOccurrence.has(key)) byOccurrence.set(key, event);
  }
  return Array.from(byOccurrence.values());
}
